import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { Card } from '@/components/ui/Card';
import { useKeyInfo, useUpdateKey } from '@/api/hooks/useKeys';
import { ApiException } from '@/api/client';

export function KeyEditPage() {
  const { key = '' } = useParams<{ key: string }>();
  const decodedKey = decodeURIComponent(key);
  const navigate = useNavigate();
  const { data, isLoading } = useKeyInfo(decodedKey);
  const update = useUpdateKey();

  const [keyAlias, setKeyAlias] = useState('');
  const [userId, setUserId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [modelsText, setModelsText] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [duration, setDuration] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setKeyAlias(data.key_alias ?? '');
      setUserId(data.user_id ?? '');
      setTeamId(data.team_id ?? '');
      setModelsText(data.models?.join(', ') ?? '');
      setMaxBudget(data.max_budget != null ? String(data.max_budget) : '');
      setDuration(data.duration ?? '');
    }
  }, [data]);

  if (isLoading || !data) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const models = modelsText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    update.mutate(
      {
        key: data.key,
        key_alias: keyAlias || null,
        user_id: userId || null,
        team_id: teamId || null,
        models: models.length ? models : null,
        max_budget: maxBudget ? Number(maxBudget) : null,
        duration: duration || null,
      },
      {
        onSuccess: () => navigate(`/keys/${encodeURIComponent(data.key)}`),
        onError: (e) =>
          setSubmitError(e instanceof ApiException ? e.message : 'Update failed'),
      },
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Edit Key</h1>
      <Card>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {submitError && (
            <div
              style={{
                padding: 12,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                color: '#991b1b',
                fontSize: 13,
              }}
            >
              {submitError}
            </div>
          )}
          <FormField label="Alias" htmlFor="key_alias">
            <Input
              id="key_alias"
              value={keyAlias}
              onChange={(e) => setKeyAlias(e.target.value)}
            />
          </FormField>
          <FormField label="User ID" htmlFor="user_id">
            <Input
              id="user_id"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </FormField>
          <FormField label="Team ID" htmlFor="team_id">
            <Input
              id="team_id"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
            />
          </FormField>
          <FormField label="Models" htmlFor="models" hint="Comma or newline separated">
            <Textarea
              id="models"
              value={modelsText}
              onChange={(e) => setModelsText(e.target.value)}
            />
          </FormField>
          <FormField label="Max Budget" htmlFor="max_budget">
            <Input
              id="max_budget"
              type="number"
              value={maxBudget}
              onChange={(e) => setMaxBudget(e.target.value)}
            />
          </FormField>
          <FormField label="Duration" htmlFor="duration" hint="e.g. 30d, 24h">
            <Input
              id="duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </FormField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}